const isHtmlLikeComment = (source, index) => source.startsWith('<!--', index) || source.startsWith('-->', index);
const isWordChar = (char) => /[\w$]/.test(char);
// After these a `/` can only start a regular expression literal.
const REGEX_PRECEDING_CHARS = new Set(['(', ',', '=', ':', '[', '!', '&', '|', '?', '{', ';', '~', '^', '%', '*', '<', '>']);
const REGEX_PRECEDING_KEYWORDS = new Set(['return', 'typeof', 'void', 'delete', 'in', 'instanceof', 'new', 'throw', 'case', 'do', 'else']);
// Keywords only in some contexts; elsewhere they are ordinary names followed by division.
const CONTEXTUAL_KEYWORDS = new Set(['of', 'yield', 'await']);
const WORD_BOUNDARY_CHARS = /[\s(){}[\];,=:!&|?~^%*<>+-]/;
// After these a `/` can only be division: they end an operand, and no semicolon is inserted before a `/`.
const DIVISION_PRECEDING_CHARS = new Set([']', "'", '"', '`']);

/**
 * Whether the `/` at `index` starts a regular expression literal (true), is division (false), or cannot be told
 * apart without a parser (null: after `)`, `}`, `+`, `-`, a contextual keyword, and anything else not listed above).
 */
const startsRegex = (source, start, index) => {
  let previous = index - 1;
  while (previous >= start && /\s/.test(source[previous])) {
    previous--;
  }
  if (previous < start) {
    return true;
  }

  const char = source[previous];
  if (REGEX_PRECEDING_CHARS.has(char)) {
    return true;
  }
  if (DIVISION_PRECEDING_CHARS.has(char)) {
    return false;
  }
  if (!isWordChar(char)) {
    return null;
  }

  let wordStart = previous;
  while (wordStart > start && isWordChar(source[wordStart - 1])) {
    wordStart--;
  }
  const before = wordStart > start ? source[wordStart - 1] : '';
  if (before === '.') {
    return false;
  }
  // Only a whole word can be a keyword: `éreturn`, `#return` and `\u0072eturn` end in "return" but are names.
  if (before && !WORD_BOUNDARY_CHARS.test(before)) {
    return null;
  }
  const word = source.slice(wordStart, previous + 1);
  if (CONTEXTUAL_KEYWORDS.has(word)) {
    return null;
  }
  return REGEX_PRECEDING_KEYWORDS.has(word);
};

/** Index just past the regular expression literal whose opening `/` is at `index`, or -1 when it is not closed on its line. */
const skipRegex = (source, index) => {
  let inClass = false;
  let cursor = index + 1;
  while (cursor < source.length) {
    const char = source[cursor];
    if (char === '\n' || char === '\r') {
      return -1;
    }
    if (char === '\\') {
      cursor += 2;
      continue;
    }
    if (char === '[') {
      inClass = true;
    } else if (char === ']') {
      inClass = false;
    } else if (char === '/' && !inClass) {
      return cursor + 1;
    }
    cursor++;
  }
  return -1;
};

/**
 * Index of the `}` that closes the interpolation whose source starts at `start`, or -1 when the end cannot be
 * found with certainty. Comments, Annex B HTML-like comments and a `/` that may be either division or a regular
 * expression stop the scan, so an uncertain split is never trusted.
 */
const findInterpolationEnd = (source, start) => {
  let depth = 0;
  let index = start;

  while (index < source.length) {
    const char = source[index];

    if (isHtmlLikeComment(source, index)) {
      return -1;
    }

    if (char === '/') {
      if (source[index + 1] === '/' || source[index + 1] === '*') {
        return -1;
      }
      const isRegex = startsRegex(source, start, index);
      if (isRegex === null) {
        return -1;
      }
      if (isRegex) {
        index = skipRegex(source, index);
        if (index === -1) {
          return -1;
        }
        continue;
      }
      index++;
      continue;
    }

    if (char === "'" || char === '"') {
      index++;
      while (index < source.length && source[index] !== char) {
        if (source[index] === '\n' || source[index] === '\r') {
          return -1;
        }
        index += source[index] === '\\' ? 2 : 1;
      }
      if (index >= source.length) {
        return -1;
      }
      index++;
      continue;
    }

    if (char === '`') {
      index++;
      while (index < source.length && source[index] !== '`') {
        if (source[index] === '\\') {
          index += 2;
          continue;
        }
        if (source[index] === '$' && source[index + 1] === '{') {
          const end = findInterpolationEnd(source, index + 2);
          if (end === -1) {
            return -1;
          }
          index = end + 1;
          continue;
        }
        index++;
      }
      if (index >= source.length) {
        return -1;
      }
      index++;
      continue;
    }

    if (char === '{') {
      depth++;
    } else if (char === '}') {
      if (depth === 0) {
        return index;
      }
      depth--;
    }
    index++;
  }

  return -1;
};

/** The source of each `${…}` interpolation, or null when the template cannot be split with certainty. */
const splitInterpolations = (template) => {
  const interpolations = [];
  let index = 0;

  while (index < template.length) {
    const char = template[index];

    if (char === '\\') {
      index += 2;
      continue;
    }

    // An unescaped backtick ends the template literal, so what follows it would run as code.
    if (char === '`') {
      return null;
    }

    if (char === '$' && template[index + 1] === '{') {
      const end = findInterpolationEnd(template, index + 2);
      if (end === -1) {
        return null;
      }
      interpolations.push(template.slice(index + 2, end));
      index = end + 1;
      continue;
    }

    index++;
  }

  return interpolations;
};

/**
 * What the code runner's security check reads for an eval-mode template sent as `code` = `(params) => \`template\``:
 * the same function with the text between interpolations removed. Only the interpolations run; the text is inert
 * (for NetSuite it is SuiteScript that runs in NetSuite), and checking it refused SuiteScript's own `require(` and
 * `paged.fetch(`. When the template cannot be split with certainty the whole template is checked.
 */
export const getEvalTemplateSecurityExpression = ({ template, inputString }) => {
  const interpolations = typeof template === 'string' ? splitInterpolations(template) : null;
  if (!interpolations) {
    return template;
  }

  return `(${inputString}) => \`${interpolations.map((source) => `\${${source}}`).join('')}\``;
};
